// Per-dialect synthesizer for CREATE / ALTER / DROP SCHEMA (v2 Slice D5).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D5.
//
// Per-engine surface:
//   - mssql:  CREATE SCHEMA name [AUTHORIZATION user]
//             ALTER  → unsupported (mssql ALTER SCHEMA … TRANSFER is the
//                      D1 'move-table-schema' op, not a schema rename)
//             DROP SCHEMA [IF EXISTS] name  (no CASCADE — must be empty)
//   - pgsql:  CREATE SCHEMA [IF NOT EXISTS] name [AUTHORIZATION role]
//             ALTER SCHEMA name RENAME TO new
//             DROP SCHEMA [IF EXISTS] name [CASCADE]
//   - mysql:  unsupported — schemas == databases (use slice G1).
//   - oracle: unsupported — schemas == users (use slice F1).
//   - sqlite: unsupported — no DDL schemas.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { SchemaChange, SchemaDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

const MYSQL_HINT
  = `mysql conflates schemas with databases. Use the database-level slice `
    + `(CREATE / ALTER / DROP DATABASE — slice G1) instead.`;
const ORACLE_HINT
  = `oracle conflates schemas with users. A schema in oracle is owned by `
    + `(and named after) a user; use the user/role slice (slice F1) instead.`;
const SQLITE_HINT
  = `sqlite has no DDL schemas. Attach an alternate database file via `
    + `'ATTACH DATABASE' if a logical separation is needed.`;

function unsupported(engine: SqlEngine, op: string, hint: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${hint}`,
  );
}

function rejectIfUnsupportedEngine(engine: SqlEngine, op: string): void {
  if (engine === 'mysql') unsupported('mysql', op, MYSQL_HINT);
  if (engine === 'oracle') unsupported('oracle', op, ORACLE_HINT);
  if (engine === 'sqlite') unsupported('sqlite', op, SQLITE_HINT);
}

// ─── CREATE SCHEMA ────────────────────────────────────────────────────────

export function synthesizeCreateSchema(
  engine: SqlEngine,
  schema: SchemaDef,
): string {
  rejectIfUnsupportedEngine(engine, 'create-schema');
  const quoted = schema.quoted ?? false;
  const name = maybeQuoteIdent(engine, schema.name, quoted);
  if (engine === 'mssql') {
    if (schema.ifNotExists) {
      throw new Error(
        `applyDdlEdit: mssql create-schema does not accept 'ifNotExists' — `
          + `T-SQL CREATE SCHEMA has no IF NOT EXISTS keyword. Wrap in an `
          + `IF SCHEMA_ID('name') IS NULL EXEC('CREATE SCHEMA …') guard at `
          + `the consumer level if conditional creation is needed.`,
      );
    }
    if (schema.pgsqlAuthorization !== undefined) {
      throw new Error(
        `applyDdlEdit: mssql create-schema does not accept 'pgsqlAuthorization' `
          + `— use 'mssqlAuthorization' instead.`,
      );
    }
    let s = `CREATE SCHEMA ${name}`;
    if (schema.mssqlAuthorization !== undefined) {
      const owner = maybeQuoteIdent(engine, schema.mssqlAuthorization, quoted);
      s += ` AUTHORIZATION ${owner}`;
    }
    return s;
  }
  // pgsql
  if (schema.mssqlAuthorization !== undefined) {
    throw new Error(
      `applyDdlEdit: postgres create-schema does not accept 'mssqlAuthorization' `
        + `— use 'pgsqlAuthorization' instead.`,
    );
  }
  let s = 'CREATE SCHEMA';
  if (schema.ifNotExists) s += ' IF NOT EXISTS';
  s += ` ${name}`;
  if (schema.pgsqlAuthorization !== undefined) {
    const owner = maybeQuoteIdent(engine, schema.pgsqlAuthorization, quoted);
    s += ` AUTHORIZATION ${owner}`;
  }
  return s;
}

// ─── ALTER SCHEMA (rename only) ───────────────────────────────────────────

export function synthesizeAlterSchema(
  engine: SqlEngine,
  schemaName: string,
  quoted: boolean,
  change: SchemaChange,
): string {
  rejectIfUnsupportedEngine(engine, 'alter-schema');
  if (engine === 'mssql') {
    unsupported(
      'mssql',
      'alter-schema',
      `mssql ALTER SCHEMA is reserved for object transfer (use op:`
        + `'move-table-schema' from slice D1). T-SQL has no schema rename — `
        + `consumers create the new schema, transfer all objects, drop the old.`,
    );
  }
  // pgsql
  const oldName = maybeQuoteIdent(engine, schemaName, quoted);
  const newName = maybeQuoteIdent(engine, change.renameTo, quoted);
  return `ALTER SCHEMA ${oldName} RENAME TO ${newName}`;
}

// ─── DROP SCHEMA ──────────────────────────────────────────────────────────

export function synthesizeDropSchema(
  engine: SqlEngine,
  schemaName: string,
  quoted: boolean,
  ifExists: boolean,
  cascade: boolean,
): string {
  rejectIfUnsupportedEngine(engine, 'drop-schema');
  if (engine === 'mssql' && cascade) {
    throw new Error(
      `applyDdlEdit: mssql drop-schema does not accept 'cascade' — T-SQL `
        + `DROP SCHEMA requires the schema to be empty. Drop / transfer all `
        + `contained objects first.`,
    );
  }
  const name = maybeQuoteIdent(engine, schemaName, quoted);
  let s = 'DROP SCHEMA';
  if (ifExists) s += ' IF EXISTS';
  s += ` ${name}`;
  if (cascade) s += ' CASCADE';
  return s;
}
